import { z } from 'zod';

/**
 * Question reports (F7 AC-4.1, AC-4.2): a learner flags a theory question as wrong from the site's
 * question page or the app's review screen, without signing in (`POST /api/reports`); editors work
 * the queue in the admin (T-4.3).
 *
 * No personal data: the body names the question and the bank version, a reason code, an optional
 * short note and where it was sent from. The object is strict, so a client that adds a field (email,
 * name, user id ...) gets a 422 instead of having it stored. The API also removes email addresses and
 * phone numbers from the note before storing it.
 */

/** Why the question is wrong. Adding a reason is additive; renaming one splits the history. */
export const QUESTION_REPORT_REASONS = [
  'wrong_answer',
  'typo',
  'image',
  'outdated',
  'other',
] as const;

/** Queue states (T-4.3): new reports are `open`; `resolved` / `rejected` close them. */
export const QUESTION_REPORT_STATUSES = ['open', 'in_review', 'resolved', 'rejected'] as const;

/** The frontend a report came from. */
export const QUESTION_REPORT_SOURCES = ['site', 'app'] as const;

/** Max characters of the learner's note. */
export const QUESTION_REPORT_NOTE_MAX = 500;

export const QuestionReportReasonSchema = z.enum(QUESTION_REPORT_REASONS);
export type QuestionReportReason = z.infer<typeof QuestionReportReasonSchema>;

export const QuestionReportStatusSchema = z.enum(QUESTION_REPORT_STATUSES);
export type QuestionReportStatus = z.infer<typeof QuestionReportStatusSchema>;

export const QuestionReportSourceSchema = z.enum(QUESTION_REPORT_SOURCES);
export type QuestionReportSource = z.infer<typeof QuestionReportSourceSchema>;

/**
 * `POST /api/reports` body. `questionId` is the stable id of the content bank (`q015`, not the display
 * number) and `contentVersion` the bank the learner saw (`v1`); the API answers 422 when the pair is
 * not in its bank. Shapes only here: the bank lives in `@dcs/content`.
 */
export const QuestionReportInputSchema = z.strictObject({
  questionId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,31}$/, 'Invalid question id'),
  contentVersion: z
    .string()
    .max(16)
    .regex(/^v\d+(?:\.\d+)*$/, 'Invalid content version'),
  reason: QuestionReportReasonSchema,
  note: z.string().max(QUESTION_REPORT_NOTE_MAX).optional(),
  source: QuestionReportSourceSchema,
});
export type QuestionReportInput = z.infer<typeof QuestionReportInputSchema>;

/** `201` answer of `POST /api/reports`: the id of the stored report. */
export const QuestionReportCreatedSchema = z.strictObject({ id: z.uuid() });
export type QuestionReportCreated = z.infer<typeof QuestionReportCreatedSchema>;

/** Max characters of the staff note an editor writes when changing a report's status (T-4.3). */
export const QUESTION_REPORT_STAFF_NOTE_MAX = 1000;

/**
 * Status changes the admin queue allows (T-4.3), by current status. A new report is `open`; an editor
 * takes it (`in_review`) and closes it (`resolved` / `rejected`); a closed report can only be reopened.
 * `open` and `in_review` may keep their status to add or change the staff note.
 */
export const QUESTION_REPORT_TRANSITIONS: Readonly<
  Record<QuestionReportStatus, readonly QuestionReportStatus[]>
> = {
  open: ['open', 'in_review', 'resolved', 'rejected'],
  in_review: ['in_review', 'open', 'resolved', 'rejected'],
  resolved: ['open'],
  rejected: ['open'],
};

/**
 * `PUT /api/admin/reports/:id` body. `expectedStatus` guards against a stale page. `staffNote` left out keeps the stored note; an empty string or
 * `null` clears it. Strict: an unknown field is a 422.
 */
export const QuestionReportStatusChangeSchema = z.strictObject({
  status: QuestionReportStatusSchema,
  /**
   * The status the editor saw when opening the report. When the stored status is another one (someone
   * else changed the report meanwhile) nothing is written and the answer is 409 `report_changed`.
   */
  expectedStatus: QuestionReportStatusSchema.optional(),
  staffNote: z
    .string()
    .trim()
    .max(QUESTION_REPORT_STAFF_NOTE_MAX)
    .nullable()
    .optional()
    .transform((note) => (note === '' ? null : note)),
});
export type QuestionReportStatusChange = z.input<typeof QuestionReportStatusChangeSchema>;
